import { ConfirmDialog } from './dialogs/confirm/confirm.dialog';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { NgModule } from '@angular/core';
import {
  CommonModule,
  DecimalPipe,
  PercentPipe,
  CurrencyPipe
} from '@angular/common';
import { OverlayModule } from '@angular/cdk/overlay';
import { LayoutModule } from '@angular/cdk/layout';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { NgxTrimDirectiveModule, NgxTrimDirective } from 'ngx-trim-directive';
import { BreadCrumbComponent } from './components/bread-crumb/bread-crumb.component';
import { FormFieldComponent } from './components/form-field/form-field.component';
import { InputDirective } from './directives/input.directive';
import { FormDirective } from './directives/form.directive';
import { ErrorMessageComponent } from './components/error-message/error-message.component';
import { FieldIndicatorComponent } from './components/field-indicator/field-indicator.component';
import { LoadingDialog } from './dialogs/loading/loading.dialog';
import { CheckboxInputComponent } from './components/checkbox-input/checkbox-input.component';
import { RadioInputComponent } from './components/radio-input/radio-input.component';
import { MatMenuModule } from '@angular/material/menu';
import { OrdinalPipe } from './pipes/ordinal.pipe';
import { RouterModule } from '@angular/router';
import { CloseDialogDirective } from './directives/close-dialog.directive';

import { PasswordFormComponent } from './components/password-form/password-form.component';
import { DialogHeaderComponent } from './components/dialog-header/dialog-header.component';
import { SelectableListComponent } from './components/selectable-list/selectable-list.component';
import { SingleSelectionListComponent } from './components/single-selection-list/single-selection-list.component';
import { NgxMaskModule } from 'ngx-mask';
import { SnackMessageComponent } from './components/snack-message/snack-message.component';
import { CalendarPickerComponent } from './components/calendar-picker/calendar-picker.component';
import { TooltipComponent } from './components/tooltip/tooltip.component';
import { TooltipTriggerForDirective } from './components/tooltip/tooltip-trigger-for.directive';
import { ClickOutsideModule } from 'ng-click-outside';
import { DecimalFormatterDirective } from './directives/decimal-formatter.directive';
import { ClearFormControlDirective } from './directives/clear-form-control.directive';
import { PaginationComponent } from './components/pagination/pagination.component';
import { SidebarModule } from 'primeng/sidebar';
import { FilterChipComponent } from './components/filter-chip/filter-chip.component';
import { FilterChipContainerComponent } from './components/filter-chip-container/filter-chip-container.component';
import { PopoverComponent } from './components/popover/popover.component';
import { DownloadPopoverComponent } from './components/download-popover/download-popover.component';
import { NavBarLogoComponent } from './components/nav-bar-logo/nav-bar-logo.component';
import { LoginIdDialog } from './dialogs/login-id/login-id.dialog';
import { PasswordDialog } from './dialogs/password/password.dialog';
import { FinanceDialog } from './dialogs/finance/finance.dialog';
import { RolesDialog } from './dialogs/roles/roles.dialog';
import { CalendarPickerPopoverComponent } from './components/calendar-picker-popover/calendar-picker-popover.component';
import { SelectionPopoverComponent } from './components/selection-popover/selection-popover.component';
import { StoreSelectionDialog } from './dialogs/store-selection/store-selection.dialog';
import { IntegerInputDirective } from './directives/integer-input.directive';
import { SvgPrinterIconComponent } from './components/svg-printer-icon/svg-printer-icon.component';
import { SpinnerComponent } from './components/spinner/spinner.component';
import { StatusLightComponent } from './components/status-light/status-light.component';
import { UserRoleTypePipe } from './pipes/userRoleType.pipe';
import { SvgRefreshIconComponent } from './components/svg-refresh-icon/svg-refresh-icon.component';
import { MobileNumberPipe } from './pipes/mobileNumber.pipe';
import { StoreSearchDialog } from './dialogs/store-search/store-search.dialog';

@NgModule({
  declarations: [
    BreadCrumbComponent,
    FormFieldComponent,
    InputDirective,
    FormDirective,
    IntegerInputDirective,
    ErrorMessageComponent,
    ConfirmDialog,
    FieldIndicatorComponent,
    LoadingDialog,
    CheckboxInputComponent,
    RadioInputComponent,
    OrdinalPipe,
    UserRoleTypePipe,
    MobileNumberPipe,
    CloseDialogDirective,
    PasswordFormComponent,
    DialogHeaderComponent,
    SelectableListComponent,
    SingleSelectionListComponent,
    SnackMessageComponent,
    CalendarPickerComponent,
    TooltipComponent,
    TooltipTriggerForDirective,
    DecimalFormatterDirective,
    ClearFormControlDirective,
    PaginationComponent,
    FilterChipComponent,
    FilterChipContainerComponent,
    PopoverComponent,
    DownloadPopoverComponent,
    NavBarLogoComponent,
    LoginIdDialog,
    PasswordDialog,
    RolesDialog,
    FinanceDialog,
    StoreSelectionDialog,
    CalendarPickerPopoverComponent,
    SelectionPopoverComponent,
    SvgPrinterIconComponent,
    SvgRefreshIconComponent,
    SpinnerComponent,
    StatusLightComponent,
    StoreSearchDialog
  ],
  imports: [
    CommonModule,
    TranslateModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    OverlayModule,
    LayoutModule,
    MatDialogModule,
    MatSnackBarModule,
    MatMenuModule,
    NgxTrimDirectiveModule,
    ClickOutsideModule,
    SidebarModule,
    NgxMaskModule.forRoot({
      dropSpecialCharacters: false
      // Doesn't work in production, issue with config token.
      // ,
      // patterns: {
      //   '0': { pattern: new RegExp(/\d/) },
      //   '9': { pattern: new RegExp(/\d/), optional: true },
      //   'A': { pattern: new RegExp(/[a-zA-Z0-9]/) },
      //   'S': { pattern: new RegExp(/[a-zA-Z]/) },
      //   '1': { pattern: new RegExp(/[1-9]/)}
      // }
    })
  ],
  exports: [
    TranslateModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    OverlayModule,
    LayoutModule,
    MatDialogModule,
    MatSnackBarModule,
    MatMenuModule,
    NgxMaskModule,
    ClickOutsideModule,
    BreadCrumbComponent,
    FormFieldComponent,
    InputDirective,
    FormDirective,
    IntegerInputDirective,
    ErrorMessageComponent,
    FieldIndicatorComponent,
    NgxTrimDirective,
    CheckboxInputComponent,
    RadioInputComponent,
    OrdinalPipe,
    UserRoleTypePipe,
    MobileNumberPipe,
    CloseDialogDirective,
    PasswordFormComponent,
    DialogHeaderComponent,
    SelectableListComponent,
    SingleSelectionListComponent,
    CalendarPickerComponent,
    SidebarModule,
    TooltipComponent,
    TooltipTriggerForDirective,
    DecimalFormatterDirective,
    ClearFormControlDirective,
    PaginationComponent,
    FilterChipComponent,
    FilterChipContainerComponent,
    PopoverComponent,
    DownloadPopoverComponent,
    NavBarLogoComponent,
    LoginIdDialog,
    PasswordDialog,
    RolesDialog,
    FinanceDialog,
    StoreSelectionDialog,
    StoreSearchDialog,
    CalendarPickerPopoverComponent,
    SelectionPopoverComponent,
    SvgPrinterIconComponent,
    SvgRefreshIconComponent,
    SpinnerComponent,
    StatusLightComponent
  ],

  entryComponents: [
    ConfirmDialog,
    LoadingDialog,
    SnackMessageComponent,
    PopoverComponent,
    DownloadPopoverComponent,
    LoginIdDialog,
    PasswordDialog,
    FinanceDialog,
    RolesDialog,
    StoreSelectionDialog,
    StoreSearchDialog,
    CalendarPickerPopoverComponent,
    SelectionPopoverComponent,
    SpinnerComponent
  ],
  providers: [DecimalPipe, PercentPipe, CurrencyPipe]
})
export class SharedModule {}
